import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {LoginDto, ResponseError} from "../../../api_client";
import {BasePathUrl} from "../../../appsettings";
import {useApi} from "../../../hooks/useApi";
import {State} from "../../../redux/store";
import {authStore} from "../data";
import {ILoadingState, IRestore} from "../data/authStore";
import {getAllPermissions} from "../../../hooks/useUserPermissions";
import {t} from "i18next";
import {
  eventsBroadcastChannel,
  Events,
  type ImpersonatePayload
} from "../../../utils/eventsBroadcastChannel";
import {
  getEndImpersonationUrl,
  getImpersonateUrl,
  getSpaLoginPath,
  parseReturnUrl
} from "../../../utils/returnUrl";
import {useMemo} from "react";

const getLogoutUrl = (): string =>
  new URL(`${BasePathUrl}/ui-api/auth/logout`, globalThis.location.origin).toString();


export const useAuthorization = () => {
  const {authApi, usersApi} = useApi();
  const {actions} = authStore;
  const dispatch = useDispatch();

  const beginLoading = (type: keyof ILoadingState) => {
    dispatch(actions.setError(undefined));
    dispatch(actions.setLoading({type: type, isLoading: true}));
  };

  const endLoading = (type: keyof ILoadingState) => {
    dispatch(actions.setLoading({type: type, isLoading: false}));
  };

  const setError = (err: any) => {
    dispatch(actions.setError(err));
  };

  const setRestore = (restore: IRestore) => {
    dispatch(actions.setRestore(restore));
  };

  const clearError = () => {
    dispatch(actions.setError(undefined));
  };

  const getUserClaims = async () => {
    const userClaims = await authApi.uiApiAuthUserPermissionsGet();
    dispatch(actions.setUserClaims(userClaims));
    if (getAllPermissions(userClaims).moduleAccess) {
      dispatch(actions.setIsAuth(true));
    } else {
      setError({
        code: "USER_PERMISSION_ERR",
        message: t("messages.userAccessIsNotAllowed")
      });
      dispatch(actions.setIsAuth(false));
    }
  };

  const getUserInfo = async () => {
    const user = await usersApi.uiApiUsersCurrentGet();

    dispatch(actions.setUser(user));
  };

  const signIn = async (model: LoginDto) => {
    try {
      beginLoading("signIn");
      await authApi.uiApiAuthLoginPost({loginDto: model});
      const returnUrl = parseReturnUrl();
      if (returnUrl) {
        globalThis.location.replace(returnUrl);
      } else {
        await getUserClaims();
        await getUserInfo();
      }
    } catch (error) {
      if (error instanceof ResponseError) {
        error = await error.response.json();
      }
      setError(error);
    } finally {
      endLoading("signIn");
    }
  };

  const signOut = (): void => {
    beginLoading("signOut");
    eventsBroadcastChannel.schedule(Events.logout);
    globalThis.location.href = getLogoutUrl();
  };

  /**
   * Вход администратора в модуль под другим пользователем.
   * Browser GET /Account/Impersonate → SignIn + Set-Cookie + 302:
   * сессия и OIDC-тикеты обновляются через полную навигацию под новой cookie.
   * redirectUrl = login: у пользователя без access=accounts ожидаем LoginSessionPanel;
   * при наличии module access NotAuthRoute уведёт на /users.
   */
  const impersonateUser = async (
    targetUserId: string,
    targetUserName?: string | null
  ): Promise<void> => {
    const adminUserId = user?.id;
    const adminUserName = user?.userName;

    if (!adminUserId || !adminUserName) {
      throw new Error(t("impersonate.adminNotResolved"));
    }

    const payload: ImpersonatePayload = {
      adminUserId,
      adminUserName,
      impersonatedUserId: targetUserId,
      impersonatedUserName: targetUserName ?? targetUserId
    };

    beginLoading("impersonateUser");

    try {
      eventsBroadcastChannel.schedule(Events.impersonate, payload);
      globalThis.location.href = getImpersonateUrl(targetUserId, getSpaLoginPath());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Impersonate user error:", error);
      endLoading("impersonateUser");
      throw error;
    }
  };

  const endImpersonation = (): void => {
    beginLoading("endImpersonation");
    eventsBroadcastChannel.schedule(Events.impersonateEnd);
    globalThis.location.href = getEndImpersonationUrl();
  };

  const error = useSelector((state: State) => state.auth.error, shallowEqual);
  const restore = useSelector((state: State) => state.auth.restore, shallowEqual);
  const loading = useSelector((state: State) => state.auth.loading, shallowEqual);
  const user = useSelector((state: State) => state.auth.data.user, shallowEqual);

  const isImpersonating = useMemo<boolean>(() =>
    Boolean(user?.impersonatingUserLogin && user.impersonatingUserLogin !== "-"),
    [user?.impersonatingUserLogin]
  );

  return {
    isImpersonating,
    signIn,
    signOut,
    setRestore,
    clearError,
    error,
    loading,
    restore,
    user,
    impersonateUser,
    endImpersonation
  };
};
